import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { Toaster } from 'react-hot-toast';

import { store } from '@/store';
import { createDynamicTheme, getTimeBasedTheme } from '@/styles/theme-dynamic';
import { selectTheme, selectThemeMode, updateAutoTheme } from '@/store/slices/uiSlice';
import { AuthProvider } from '@/components/providers/AuthProvider';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { getCurrentEnvironment } from '@/config/environment';

// Import critical visibility fixes
import '@/styles/visibility-fixes.css';

// Import global error handling
import { initializeGlobalErrorHandling } from '@/utils/globalErrorHandler';
// Import service utilities to ensure they're available globally
import '@/utils/serviceIconUtils';
// Import token debug utility for browser console
import '@/utils/tokenDebug';

// Import testing utilities in development
if (import.meta.env.MODE === 'development') {
  import('@/utils/validateDashboard');
  import('@/utils/clearDevAuth');
  import('@/utils/authDebug');
  import('@/utils/debugPermissions');
}

// Initialize global error handling
initializeGlobalErrorHandling();

// Layout Components
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';

// Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { OTPLoginPage } from '@/pages/auth/OTPLoginPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { MFAVerifyPage } from '@/pages/auth/MFAVerifyPage';
import { MFASetupPage } from '@/pages/auth/MFASetupPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { BookingsPage } from '@/pages/bookings/BookingsPage';
import { FleetManagementHub } from '@/pages/vehicles/FleetManagementHub';
import ApprovalDashboardMUI from '@/pages/approval/ApprovalDashboardMUI';
import { DriversPage } from '@/pages/drivers/DriversPage';
import { UsersPage } from '@/pages/users/UsersPage';
import { VerificationPage } from '@/pages/verification/VerificationPage';
import { DisputesPage } from '@/pages/disputes/DisputesPage';
import { AnalyticsPage } from '@/pages/analytics/AnalyticsPage';
import { PartnerManagementPage } from '@/pages/partners/PartnerManagementPage';
import { ExperimentationToolsPage } from '@/pages/experiments/ExperimentationToolsPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import CarouselManagementPage from '@/pages/settings/CarouselManagementPage';
import { VehicleTypesSettingsPage } from '@/pages/settings/VehicleTypesSettingsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import PaymentCallback from '@/pages/payment/PaymentCallback';
import PaymentVerificationPage from '@/pages/payment/PaymentVerificationPage';
import ComplianceMetricsPage from '@/pages/compliance/ComplianceMetricsPage';
import HostEducationNudgesPage from '@/pages/education/HostEducationNudgesPage';
import SLAMonitoringPage from '@/pages/monitoring/SLAMonitoringPage';
import RegionalCompliancePage from '@/pages/compliance/RegionalCompliancePage';
import DemandIntelligencePage from '@/pages/intelligence/DemandIntelligencePage';
import AuditTrailPage from '@/pages/audit/AuditTrailPage';
import FollowUpDashboardPage from '@/pages/dashboard/FollowUpDashboardPage';
import PushNotificationPage from '@/pages/notifications/PushNotificationPage';
import AdminNotificationsPage from '@/pages/notifications/AdminNotificationsPage';
import TripSharingManagementPage from '@/pages/security/TripSharingManagementPage';
import AccessCodeManagementPage from '@/pages/security/AccessCodeManagementPage';
import SecurityMonitoringPage from '@/pages/security/SecurityMonitoringPage';
import { DataCleanupPage } from '@/pages/admin/DataCleanupPage';
import MarketingMessagesPage from '@/pages/marketing/MarketingMessagesPage';
import BroadcastMessagingPage from '@/pages/marketing/BroadcastMessagingPage';
import { UserProfilePage } from '@/pages/user/UserProfilePage';
import HostVehicleDashboard from '@/pages/hostVehicles/HostVehicleDashboard';
import FleetManagerMonitoringPage from '@/pages/fleet/FleetManagerMonitoringPage';
import { ReviewsDashboardPage } from '@/pages/reviews/ReviewsDashboardPage';
import FleetOwnersPage from '@/pages/fleet/FleetOwnersPage';
import ReferralProgramAdminPage from '@/pages/referrals/ReferralProgramAdminPage';
import ReferralsPage from '@/pages/referrals/ReferralsPage';
import BetaInvitationsPage from '@/pages/invitations/BetaInvitationsPage';
import WaitlistRequestsPage from '@/pages/waitlist/WaitlistRequestsPage';
import FavoritesManagementPage from '@/pages/favorites/FavoritesManagementPage';
import PromoCodesPage from '@/pages/promo-codes/PromoCodesPage';
import ModerationDashboard from '@/pages/moderation/ModerationDashboard';
import DriverCampaignsPage from '@/pages/campaigns/DriverCampaignsPage';
import VehicleCampaignsPage from '@/pages/campaigns/VehicleCampaignsPage';
import FinancialManagementPage from '@/pages/finances/FinancialManagementPage';
import TestDataInitializationPage from '@/pages/settings/TestDataInitializationPage';
import DisplayNameReviewPage from '@/pages/display-names/DisplayNameReviewPage';
import VerificationPhotoReviewPage from '@/pages/verification/VerificationPhotoReviewPage';
import FleetSubscriptionManagement from '@/pages/fleet-subscriptions/FleetSubscriptionManagement';
import BankVerificationPage from '@/pages/security/BankVerificationPage';
import AdminManagementPage from '@/pages/admin/AdminManagementPage';
import RolePermissionsPage from '@/pages/admin/RolePermissionsPage';
import AdminAlertsPage from '@/pages/admin/AdminAlertsPage';
import { CorporateAccountsPage } from '@/pages/admin/CorporateAccountsPage';
import AirportManagementPage from '@/pages/service-areas/AirportManagementPage';
import ZoneManagementPage from '@/pages/service-areas/ZoneManagementPage';
import AirportPricingManagementPage from '@/pages/pricing/AirportPricingManagementPage';
import YachtDestinationPricingManagementPage from '@/pages/pricing/YachtDestinationPricingManagementPage';
import YachtDestinationsPage from '@/pages/operations/YachtDestinationsPage';
import PartnershipsListPage from '@/pages/admin/PartnershipsListPage';
import PartnershipApplicationsPage from '@/pages/admin/PartnershipApplicationsPage';
import PartnershipSettingsPage from '@/pages/admin/PartnershipSettingsPage';
import { FilterManagementPage } from '@/pages/admin/FilterManagementPage';

// Import redirect helpers
import {
  UserManagementRedirect
} from '@/components/navigation/RedirectHelper';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Theme wrapper component that handles auto theme switching
const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector(selectTheme);
  const themeMode = useSelector(selectThemeMode);
  
  // Auto theme switching effect
  useEffect(() => {
    if (themeMode === 'auto') {
      const updateTheme = () => {
        const timeBasedTheme = getTimeBasedTheme();
        dispatch(updateAutoTheme(timeBasedTheme));
      };
      
      // Update theme immediately
      updateTheme();
      
      // Set up interval to check every minute
      const interval = setInterval(updateTheme, 60000);
      
      return () => clearInterval(interval);
    }
  }, [dispatch, themeMode]);
  
  // Apply theme data attribute for CSS targeting
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);
  
  const theme = createDynamicTheme(currentTheme);
  const environment = getCurrentEnvironment();
  const showWatermark = environment === 'staging';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}

      {/* Staging Watermark - Visible on ALL pages */}
      {showWatermark && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: { xs: '60px', sm: '80px', md: '120px' },
            fontWeight: 900,
            color: currentTheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 9999,
            whiteSpace: 'nowrap',
            letterSpacing: '0.1em',
          }}
        >
          STAGING
        </Box>
      )}
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeWrapper>
          <Router>
            <AuthProvider>
              <Routes>
                {/* Auth Routes */}
                <Route path="/auth" element={<AuthLayout />}>
                  <Route path="login" element={<LoginPage />} />
                  <Route path="otp-login" element={<OTPLoginPage />} />
                  <Route path="forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="mfa-verify" element={<MFAVerifyPage />} />
                  <Route path="mfa-setup" element={<MFASetupPage />} />
                  <Route path="" element={<Navigate to="/auth/otp-login" replace />} />
                </Route>

                {/* Payment Callback Routes (Public) */}
                <Route path="/payment/callback" element={<PaymentVerificationPage />} />
                <Route path="/payment-success" element={<PaymentVerificationPage />} />
                <Route path="/payment/verify" element={<PaymentVerificationPage />} />

                {/* Protected Dashboard Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  
                  {/* Bookings */}
                  <Route path="bookings/*" element={<BookingsPage />} />
                  
                  {/* Vehicles */}
                  <Route path="vehicles/*" element={<FleetManagementHub />} />
                  <Route path="approvals" element={<ApprovalDashboardMUI />} />
                  <Route path="host-vehicles" element={<HostVehicleDashboard />} />
                  <Route path="fleet-managers" element={<FleetManagerMonitoringPage />} />
                  <Route path="fleet-owners" element={<FleetOwnersPage />} />
                  <Route path="fleet-subscriptions" element={<FleetSubscriptionManagement />} />

                  {/* Drivers */}
                  <Route path="drivers/*" element={<DriversPage />} />

                  {/* Users */}
                  <Route path="users/*" element={<UsersPage />} />
                  <Route path="display-names" element={<DisplayNameReviewPage />} />
                  <Route path="profile" element={<UserProfilePage />} />

                  {/* Legacy Redirects - Guide users to unified interfaces */}
                  <Route path="users/management" element={<UserManagementRedirect />} />
                  
                  {/* Operations */}
                  <Route path="verification/*" element={<VerificationPage />} />
                  <Route path="verification/photos/:userId" element={<VerificationPhotoReviewPage />} />
                  <Route path="follow-up" element={<FollowUpDashboardPage />} />
                  <Route path="compliance" element={<ComplianceMetricsPage />} />
                  <Route path="regional-compliance" element={<RegionalCompliancePage />} />
                  <Route path="education" element={<HostEducationNudgesPage />} />
                  <Route path="marketing/messages" element={<MarketingMessagesPage />} />
                  <Route path="marketing/broadcasts" element={<BroadcastMessagingPage />} />
                  <Route path="referrals" element={<ReferralsPage />} />
                  <Route path="referral-programs" element={<ReferralProgramAdminPage />} />
                  <Route path="invitations" element={<BetaInvitationsPage />} />
                  <Route path="waitlist" element={<WaitlistRequestsPage />} />
                  <Route path="promo-codes" element={<PromoCodesPage />} />
                  <Route path="driver-campaigns" element={<DriverCampaignsPage />} />
                  <Route path="vehicle-campaigns" element={<VehicleCampaignsPage />} />
                  <Route path="carousels" element={<CarouselManagementPage />} />
                  <Route path="favorites" element={<FavoritesManagementPage />} />
                  <Route path="sla-monitoring" element={<SLAMonitoringPage />} />
                  <Route path="demand-intelligence" element={<DemandIntelligencePage />} />
                  <Route path="audit-trail" element={<AuditTrailPage />} />
                  <Route path="disputes/*" element={<DisputesPage />} />
                  <Route path="reviews" element={<ReviewsDashboardPage />} />
                  <Route path="operations/yacht-destinations" element={<YachtDestinationsPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="finances" element={<FinancialManagementPage />} />
                  <Route path="partners" element={<PartnerManagementPage />} />
                  <Route path="experiments" element={<ExperimentationToolsPage />} />
                  <Route path="alerts" element={<FollowUpDashboardPage />} />
                  
                  {/* Notifications */}
                  <Route path="push-notifications" element={<AdminNotificationsPage />} />
                  
                  {/* Security */}
                  <Route path="security/bank-verification" element={<BankVerificationPage />} />
                  <Route path="trip-sharing" element={<TripSharingManagementPage />} />
                  <Route path="access-codes" element={<AccessCodeManagementPage />} />
                  <Route path="security-monitoring" element={<SecurityMonitoringPage />} />

                  {/* Moderation */}
                  <Route path="moderation" element={<ModerationDashboard />} />

                  {/* Settings */}
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="settings/vehicle-types" element={<VehicleTypesSettingsPage />} />
                  <Route path="test-data-initialization" element={<TestDataInitializationPage />} />

                  {/* Service Areas - Airport Trips */}
                  <Route path="service-areas/airports" element={<AirportManagementPage />} />
                  <Route path="service-areas/airports/:airportId/zones" element={<ZoneManagementPage />} />
                  <Route path="pricing/airport-transfers" element={<AirportPricingManagementPage />} />

                  {/* Yacht Destination Pricing */}
                  <Route path="pricing/yacht-destinations" element={<YachtDestinationPricingManagementPage />} />

                  {/* Admin Tools */}
                  <Route path="admin/staff" element={<AdminManagementPage />} />
                  <Route path="admin/roles" element={<RolePermissionsPage />} />
                  <Route path="admin-alerts" element={<AdminAlertsPage />} />
                  <Route path="admin/corporate-accounts" element={<CorporateAccountsPage />} />
                  <Route path="admin/partnerships" element={<PartnershipsListPage />} />
                  <Route path="admin/partnership-applications" element={<PartnershipApplicationsPage />} />
                  <Route path="admin/partnership-settings" element={<PartnershipSettingsPage />} />
                  <Route path="admin/filters" element={<FilterManagementPage />} />
                  <Route path="admin/data-cleanup" element={<DataCleanupPage />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AuthProvider>
          </Router>
          
          {/* Global Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#4caf50',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#f44336',
                  secondary: '#fff',
                },
              },
            }}
          />
        </ThemeWrapper>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;